import { z } from 'zod'

// US ZIP: five digits, optionally followed by a dash and four digits.
// `ZipInput` strips everything except digits and dashes at the input layer;
// this regex validates the final structure on submit. Bounded quantifiers
// — no ReDoS risk; eslint-disable suppresses the conservative safe-regex
// heuristic that flags every regex with grouped repetition.
// eslint-disable-next-line security/detect-unsafe-regex
export const US_ZIP_REGEX = /^\d{5}(?:-\d{4})?$/
export const US_ZIP_MESSAGE = 'Zip code must be 5 digits, optionally followed by -4 digits'

// Zod 4 dropped `required_error`. This keeps its meaning: the message is for a
// missing value, and any other type error keeps zod's own message.
export function requiredError(message: string): {
  error: (issue: { input?: unknown }) => string | undefined
} {
  return { error: (issue) => (issue.input === undefined ? message : undefined) }
}

// An empty number field holds '', null or undefined; each counts as no value,
// so the schema's required message shows instead of a range error for 0.
export function toOptionalNumber(val: unknown): unknown {
  return val === '' || val === undefined || val === null ? undefined : Number(val)
}

export function optionalCappedString(max: number, fieldName: string) {
  return z.string().max(max, `${fieldName} is too long`).optional().default('')
}

// Counted as the user sees the message, with each line break as one character.
// 1,500 fits every message on prod (the longest was 1,213 on 2026-10-21);
// api-core sets no limit (web-portal#111).
export const RECEIPT_MESSAGE_MAX_LENGTH = 1500

function toNumberOrNull(val: unknown): unknown {
  if (val === '' || val === null || val === undefined) return null
  if (typeof val === 'number') return val
  const parsed = Number(val)
  return Number.isFinite(parsed) ? parsed : val
}

// Typed as the field's value (a `DecimalInput`): @hookform/resolvers 5 types the
// form's fields from the schema's input, and an untyped preprocess is `unknown`.
export const optionalNumberOrNull = z.preprocess(
  (val: number | null) => toNumberOrNull(val),
  z.number().nullable()
)
